/**
 * The two letters the neighbours get, and the packet Kelsey prints them from.
 *
 * Jonathon bought the Roof Strategist letters and filled them in on
 * 2026-09-15; the two that send themselves live in Marketing Battle Pack 2.0
 * and are linked from `company_settings`. A replacement being signed earns the
 * Install Notice, and the same job's roof being finished earns "We Did Your
 * Neighbor's Roof". Repairs earn nothing. See ADR 0149.
 *
 * This module decides everything about a mailing that can be decided without a
 * network: which of the four master letters the job earns, what goes into its
 * placeholders, what the cover sheet says, what the email to Kelsey says, and
 * when a failed batch is tried again. The parts that are not decisions — the
 * Google Docs merge, the address lookup, the PDFs — are next door in
 * `labelSheet.ts` and in the Edge Function.
 *
 * ## What changed, and why this file no longer sends a link
 *
 * The first version of this emailed Kelsey a link to the master document and
 * asked her to type the customer's name into it, pull twenty addresses
 * herself, and print. Josh's review of PR #257 replaced that with a finished
 * packet: Roof Board fills a temporary copy of the master, exports the PDF,
 * finds the addresses, prints labels, and leaves Kelsey the part that needs
 * hands. The master is never personalised now — the reason the old note ended
 * with a warning about the last job's name still being in it.
 */

import { coverSheetPdf, type CoverSheet } from "./labelSheet.js";
import { coordinate } from "./neighbourAddresses.js";

/** Which letter is owed. Matches `neighbour_letters.kind`. */
export type NeighbourLetterKind = "install_notice" | "work_complete";

/**
 * How many neighbours get one. Jonathon, 2026-09-15: "give her the 20 closest
 * street addresses", and closest **by distance**, not same-street-only — asked
 * and answered the same day. Twenty nearest houses can wrap around a corner
 * onto three streets and that is what he wants.
 */
export const NEIGHBOUR_LETTER_COUNT = 20;

/**
 * Where the packets go when `company_settings.neighbour_letters_to` is blank.
 * A fallback rather than the only answer, so the address can move without a
 * deploy, and a blank row never silently sends to nobody.
 */
export const NEIGHBOUR_LETTERS_TO = "kelsey@copperridge-roofing.com";

/** The settings columns this reads. */
export interface NeighbourLetterSettings {
  neighbour_letters_enabled?: boolean | null;
  neighbour_letters_to?: string | null;
  install_notice_retail_url?: string | null;
  install_notice_storm_url?: string | null;
  work_complete_retail_url?: string | null;
  work_complete_storm_url?: string | null;
  /** Off until RentCast has been piloted on real jobs — see ADR 0149. */
  neighbour_address_lookup_enabled?: boolean | null;
  neighbour_label_stock?: string | null;
}

/** What a packet needs to know about the job. */
export interface NeighbourLetterJob {
  job_number?: number | string | null;
  bid_type?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  customer_name?: string | null;
  lat?: number | null;
  lng?: number | null;
}

export type NeighbourLetterRefusal =
  | "switched_off"
  | "not_a_replacement"
  | "no_link"
  | "no_address"
  | "not_a_google_doc"
  | "no_coordinates"
  | "lookup_off"
  | "missing_field";

/** Why nothing was sent, in a sentence staff can read on the row. */
export const NEIGHBOUR_LETTER_REFUSALS: Record<NeighbourLetterRefusal, string> =
  {
    switched_off: "Neighbour letters are switched off in Tools.",
    not_a_replacement:
      "Repairs do not get a neighbour letter — replacements only.",
    no_link:
      "No letter is linked in Tools for this kind of job, so there is nothing " +
      "to fill in.",
    no_address: "The job has no address, so there is no street to name.",
    not_a_google_doc:
      "The letter linked in Tools is not a Google Doc, so it cannot be " +
      "filled in. Paste the document's own link.",
    no_coordinates:
      "This job's address has never been pinned on the map, so the closest " +
      "houses cannot be found. Re-pick the address on the job.",
    lookup_off:
      "Neighbour address lookup has not been switched on yet in Tools.",
    missing_field:
      "The letter still has a blank in it after filling in — see the row for " +
      "which one.",
  };

export interface NeighbourLetterNote {
  to: string;
  subject: string;
  body: string;
}

// ---------------------------------------------------------------------------
// The master document
// ---------------------------------------------------------------------------

/** What the letter is called, on the subject line and the cover sheet. */
export const LETTER_NAMES: Record<NeighbourLetterKind, string> = {
  install_notice: "Install Notice Neighborhood Announcement",
  work_complete: "We Did Your Neighbor's Roof",
};

/**
 * The document id inside a Google Docs link.
 *
 * Jonathon pastes whatever the browser's address bar holds, which is
 * `…/document/d/<id>/edit?usp=sharing`, and sometimes what the Share button
 * copies, which is the same with a different tail. A bare id is accepted
 * because it is what somebody who has read the Docs API would paste.
 */
export function documentIdFrom(url: string | null | undefined): string | null {
  const value = String(url ?? "").trim();
  if (value === "") return null;
  const match = value.match(/\/document\/d\/([A-Za-z0-9_-]{10,})/);
  if (match) return match[1] ?? null;
  const fromQuery = value.match(/[?&]id=([A-Za-z0-9_-]{10,})/);
  if (fromQuery) return fromQuery[1] ?? null;
  return /^[A-Za-z0-9_-]{20,}$/.test(value) ? value : null;
}

/**
 * The placeholders the master letters carry.
 *
 * Josh: "Use explicit placeholders such as {{customer_first_name}},
 * {{street_name}}, and {{city}}." Explicit rather than clever: a merge that
 * looks for the previous customer's name and replaces that would personalise
 * the wrong words the first time a letter was reworded, and would be
 * impossible to check. A placeholder is either there and replaced, or the
 * batch stops.
 */
export const NEIGHBOUR_PLACEHOLDERS = [
  "customer_first_name",
  "street_name",
  "city",
  "state",
  "company_name",
] as const;

export type NeighbourPlaceholder = (typeof NEIGHBOUR_PLACEHOLDERS)[number];

/** `{{street_name}}`, as it is written in the document. */
export const placeholderToken = (name: string): string => `{{${name}}}`;

/**
 * The street a house is on, with its number taken off.
 *
 * "the customer's first name, and their street name (not street address)" —
 * the letters name the street the work is on so a neighbour recognises it, and
 * printing the customer's house number on twenty strangers' post is not what
 * was asked for.
 *
 * Deliberately simple: drop a leading house number and carry on. Everything
 * else — "Apt 4", a rural route, a street that genuinely starts with a digit —
 * is left alone rather than guessed at.
 */
export function streetNameFrom(
  address: string | null | undefined,
): string | null {
  const line = (String(address ?? "").split(",")[0] ?? "")
    .trim()
    .replace(/\s+/g, " ");
  if (line === "") return null;
  // A leading house number, with or without a unit letter or fraction on it:
  // "123", "123A", "123-B", "1/2". What follows is the street.
  //
  // The letters after the digits must be either a single one ("123A") or
  // behind a separator ("123-B", "1/2"). Anything else is not a house number:
  // "4th Avenue South" is a street that happens to start with a digit, and an
  // earlier pass at this ate the "4th" and left Kelsey addressing letters to
  // "Avenue South".
  const withoutNumber = line.replace(
    /^\d+(?:[-/][0-9A-Za-z]{1,3}|[A-Za-z])?\s+/,
    "",
  );
  const street = withoutNumber.trim();
  return street === "" ? line : street;
}

/** Somebody's first name, for the one place the letters use one. */
export function firstNameFrom(name: string | null | undefined): string | null {
  const first =
    String(name ?? "")
      .trim()
      .split(/\s+/)[0] ?? "";
  return first === "" ? null : first;
}

/**
 * Which of the four master letters this job earns.
 *
 * "if the bid type is insurance it's storm. if it's retail it's retail" —
 * Jonathon, 2026-09-15. A repair earns nothing; replacements only.
 */
export function neighbourLetterLink(
  kind: NeighbourLetterKind,
  bidType: string | null | undefined,
  settings: NeighbourLetterSettings,
): string | null {
  const bid = String(bidType ?? "")
    .trim()
    .toLowerCase();
  if (bid !== "retail" && bid !== "insurance") return null;
  const storm = bid === "insurance";
  const url =
    kind === "install_notice"
      ? storm
        ? settings.install_notice_storm_url
        : settings.install_notice_retail_url
      : storm
        ? settings.work_complete_storm_url
        : settings.work_complete_retail_url;
  const trimmed = String(url ?? "").trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * What every placeholder becomes, for this job.
 *
 * The first name is filled on the install notice only. The completion letter
 * never names anybody — it says a roof on a street is finished — so putting a
 * customer's name in it would be putting it nowhere the letter reads it, and
 * the master for that kind carries no `{{customer_first_name}}` at all.
 */
export function mergeFieldsFor(
  kind: NeighbourLetterKind,
  job: NeighbourLetterJob,
  companyName: string,
): Record<NeighbourPlaceholder, string> {
  const street = streetNameFrom(job.address) ?? "";
  return {
    customer_first_name:
      kind === "install_notice" ? (firstNameFrom(job.customer_name) ?? "") : "",
    street_name: street,
    city: String(job.city ?? "").trim(),
    state: String(job.state ?? "").trim(),
    company_name: companyName,
  };
}

/**
 * The fields a letter of this kind cannot go out without.
 *
 * Josh: "Validate required values and reject unresolved placeholders before
 * marking the packet ready." A letter that reads "we are replacing a roof on
 * " is worse than a letter that did not go.
 */
export function requiredFieldsFor(
  kind: NeighbourLetterKind,
): NeighbourPlaceholder[] {
  // City and state on both (review item 9 on PR #257): a job with no city
  // printed "on Oak Ridge Dr in , ." and passed the braces check, because
  // the placeholder had been replaced — with nothing.
  return kind === "install_notice"
    ? ["customer_first_name", "street_name", "city", "state"]
    : ["street_name", "city", "state"];
}

/** The required fields that came out blank, if any. */
export function missingFields(
  kind: NeighbourLetterKind,
  fields: Record<string, string>,
): string[] {
  return requiredFieldsFor(kind).filter(
    (name) => String(fields[name] ?? "").trim() === "",
  );
}

/**
 * Placeholders still sitting in the merged text.
 *
 * Anything of the form `{{…}}`, not only the ones this module knows: a master
 * that grows a `{{roof_color}}` nobody told the code about must stop the batch
 * rather than post twenty letters with the braces showing.
 */
export function unresolvedPlaceholders(
  text: string | null | undefined,
): string[] {
  const found = String(text ?? "").match(/\{\{\s*[A-Za-z0-9_.]+\s*\}\}/g) ?? [];
  return [...new Set(found.map((token) => token.trim()))];
}

// ---------------------------------------------------------------------------
// What a batch is allowed to do
// ---------------------------------------------------------------------------

export type NeighbourLetterPlan =
  | {
      ok: true;
      kind: NeighbourLetterKind;
      templateUrl: string;
      documentId: string;
      fields: Record<NeighbourPlaceholder, string>;
      origin: { lat: number; lng: number; addressKey: string };
    }
  | { ok: false; refusal: NeighbourLetterRefusal; reason: string };

/**
 * Everything checked before a single Google API call is made.
 *
 * In one place and ahead of the work, because every one of these is a refusal
 * a person can fix — a blank field in Tools, an address never pinned on the
 * map — and finding out half way through means a temporary document left in
 * Drive for the cleanup pass to find.
 */
export function neighbourLetterPlan(
  kind: NeighbourLetterKind,
  job: NeighbourLetterJob,
  settings: NeighbourLetterSettings,
  companyName: string,
  addressKeyOf: (
    line1: string | null | undefined,
    city?: string | null,
    postalCode?: string | null,
  ) => string,
): NeighbourLetterPlan {
  const refuse = (refusal: NeighbourLetterRefusal): NeighbourLetterPlan => ({
    ok: false,
    refusal,
    reason: NEIGHBOUR_LETTER_REFUSALS[refusal],
  });

  if (settings.neighbour_letters_enabled === false)
    return refuse("switched_off");

  const bid = String(job.bid_type ?? "")
    .trim()
    .toLowerCase();
  if (bid !== "retail" && bid !== "insurance")
    return refuse("not_a_replacement");

  const templateUrl = neighbourLetterLink(kind, job.bid_type, settings);
  if (!templateUrl) return refuse("no_link");

  const documentId = documentIdFrom(templateUrl);
  if (!documentId) return refuse("not_a_google_doc");

  const street = streetNameFrom(job.address);
  if (!street) return refuse("no_address");

  // Whether the address lookup is switched on is deliberately NOT checked
  // here. The completion letter reuses the households the signing letter
  // found, so it needs no lookup at all — and a switch turned off in between
  // must not block a mailing that has its addresses already. The gate lives
  // where the provider is actually called.
  const lat = coordinate(job.lat);
  const lng = coordinate(job.lng);
  if (lat === null || lng === null) return refuse("no_coordinates");

  const fields = mergeFieldsFor(kind, job, companyName);
  const missing = missingFields(kind, fields);
  if (missing.length > 0) {
    return {
      ok: false,
      refusal: "missing_field",
      reason:
        `The letter needs ${missing.map((name) => placeholderToken(name)).join(" and ")}` +
        ", and the job has nothing to put there.",
    };
  }

  return {
    ok: true,
    kind,
    templateUrl,
    documentId,
    fields,
    origin: {
      lat,
      lng,
      addressKey: addressKeyOf(
        String(job.address ?? "").split(",")[0],
        job.city,
        job.zip,
      ),
    },
  };
}

// ---------------------------------------------------------------------------
// The packet
// ---------------------------------------------------------------------------

/** What the finished packet holds, for the note and the cover sheet. */
export interface NeighbourPacket {
  kind: NeighbourLetterKind;
  jobNumber: string;
  street: string;
  city: string;
  recipients: number;
  /** Set when fewer than twenty were found, and not a failure. */
  shortfallNote?: string | null;
  labelStockName: string;
  sheets: number;
  /** How many of the addresses are missing a city or a postcode. */
  incomplete?: number;
}

/** Where a job's packet lives in the `job-files` bucket. */
export function packetPath(
  jobId: string,
  kind: NeighbourLetterKind,
  file: "letter" | "labels" | "cover",
): string {
  return `neighbour-letters/${jobId}/${kind}-${file}.pdf`;
}

/** What the three files are called in Kelsey's inbox. */
export function packetFileName(
  packet: Pick<NeighbourPacket, "kind" | "jobNumber">,
  file: "letter" | "labels" | "cover",
): string {
  const name =
    packet.kind === "install_notice" ? "install-notice" : "roof-done";
  const job = packet.jobNumber === "" ? "job" : `job-${packet.jobNumber}`;
  const tail =
    file === "letter" ? "letter" : file === "labels" ? "labels" : "cover-sheet";
  return `${job}-${name}-${tail}.pdf`;
}

const where = (packet: NeighbourPacket): string =>
  packet.city === "" ? packet.street : `${packet.street}, ${packet.city}`;

/** The cover sheet that rides on top of the printed packet. */
export function neighbourCoverSheet(packet: NeighbourPacket): CoverSheet {
  const lines: string[] = [
    `${LETTER_NAMES[packet.kind]} — ${where(packet)}`,
    packet.jobNumber === "" ? "" : `Job ${packet.jobNumber}`,
    "",
    `${packet.recipients} ${packet.recipients === 1 ? "letter" : "letters"} to print and mail.`,
    `Labels: ${packet.labelStockName}, ${packet.sheets} ${
      packet.sheets === 1 ? "sheet" : "sheets"
    }.`,
  ];
  if (packet.shortfallNote) {
    lines.push("");
    lines.push(packet.shortfallNote);
  }
  if (packet.incomplete) {
    lines.push("");
    lines.push(
      `${packet.incomplete} of the addresses are missing a town or a ZIP. ` +
        "They are on the labels as they came back — check them before they go.",
    );
  }

  return {
    title: "Neighbour mailing",
    lines,
    steps: [
      `Print the letter ${packet.recipients} times.`,
      "Print the labels on the stock named above.",
      "Stuff, stamp and mail.",
      'Tick "Mailed" in Roof Board → Marketing → Neighbour letters.',
    ],
  };
}

/** The cover sheet as a PDF, which is the only form it is ever used in. */
export function neighbourCoverSheetPdf(packet: NeighbourPacket): Uint8Array {
  return coverSheetPdf(neighbourCoverSheet(packet));
}

/**
 * The note the packet arrives with.
 *
 * Short, because everything it used to ask Kelsey to do is now attached to it.
 * What is left is what she has to decide: how many to print, and that it went.
 */
export function neighbourPacketNote(
  packet: NeighbourPacket,
  settings: NeighbourLetterSettings,
): NeighbourLetterNote {
  const name = LETTER_NAMES[packet.kind];
  const lines: string[] = [];

  lines.push(
    packet.kind === "install_notice"
      ? `A replacement has been signed on ${where(packet)}${
          packet.jobNumber === "" ? "" : ` (job ${packet.jobNumber})`
        }.`
      : `The roof on ${where(packet)}${
          packet.jobNumber === "" ? "" : ` (job ${packet.jobNumber})`
        } is finished.`,
  );
  lines.push("");
  lines.push(
    `Attached: the ${name} letter, ready to print ${packet.recipients} times, ` +
      `address labels for ${packet.labelStockName.split(" —")[0]}, and a cover ` +
      "sheet.",
  );
  if (packet.shortfallNote) {
    lines.push("");
    lines.push(packet.shortfallNote);
  }
  lines.push("");
  lines.push(
    "When it is in the post, tick it off in Roof Board under Marketing → " +
      "Neighbour letters.",
  );

  const to =
    String(settings.neighbour_letters_to ?? "").trim() || NEIGHBOUR_LETTERS_TO;

  return {
    to,
    subject: `${name} — ${where(packet)}`,
    body: lines.join("\n"),
  };
}

/** The nudge for a packet that has been ready for a while and not gone out. */
export function neighbourReminderNote(
  packet: NeighbourPacket,
  days: number,
  settings: NeighbourLetterSettings,
): NeighbourLetterNote {
  const to =
    String(settings.neighbour_letters_to ?? "").trim() || NEIGHBOUR_LETTERS_TO;
  return {
    to,
    subject: `Still to mail: ${LETTER_NAMES[packet.kind]} — ${where(packet)}`,
    body: [
      `The neighbour letters for ${where(packet)}${
        packet.jobNumber === "" ? "" : ` (job ${packet.jobNumber})`
      } have been ready for ${days} ${days === 1 ? "day" : "days"} and have ` +
        "not been ticked off as mailed.",
      "",
      "If they have gone out, tick them off in Roof Board under Marketing → " +
        "Neighbour letters. The packet is attached to the job under Docs.",
    ].join("\n"),
  };
}

// ---------------------------------------------------------------------------
// The queue
// ---------------------------------------------------------------------------

/** What a batch is doing, as `neighbour_letters.status` holds it. */
export const NEIGHBOUR_LETTER_STATUSES = [
  "preparing",
  "ready",
  "mailed",
  "blocked",
  "canceled",
] as const;

export type NeighbourLetterStatus = (typeof NEIGHBOUR_LETTER_STATUSES)[number];

export const NEIGHBOUR_STATUS_LABELS: Record<NeighbourLetterStatus, string> = {
  preparing: "Preparing",
  ready: "Ready to print",
  mailed: "Mailed",
  blocked: "Needs attention",
  canceled: "Canceled",
};

/**
 * How long a worker's claim on a row lasts.
 *
 * An Edge Function that is killed half way through leaves its claim behind,
 * and a claim nothing can take back is a letter that never goes. Two minutes
 * is comfortably longer than a packet takes to build — a Docs copy, a merge,
 * an export, a lookup and a send — and short enough that a death costs one
 * sweep.
 */
export const NEIGHBOUR_LEASE_SECONDS = 120;

/**
 * How many times a batch is tried before it is left for a person.
 *
 * Josh: "Track attempts and retry eligibility so blocked records cannot starve
 * newer work." Attempts are what makes that possible — a row that has used
 * them up goes to `blocked`, which the claim query does not look at, so
 * twenty-five hopeless rows cannot fill a batch of twenty-five for ever.
 */
export const NEIGHBOUR_MAX_ATTEMPTS = 6;

/**
 * How long to wait before trying a failed batch again, in seconds.
 *
 * Backing off rather than hammering: the commonest failures are a Google token
 * being renewed, a rate limit and a lookup provider having a bad minute, and
 * all three are better in a minute than in a second.
 */
export function neighbourRetrySeconds(attempts: number): number {
  const ladder = [60, 300, 900, 3600, 10800];
  const index = Math.max(0, Math.min(ladder.length - 1, attempts - 1));
  return ladder[index]!;
}

/** Where a failed attempt leaves the row. */
export function afterFailure(attempts: number): {
  status: NeighbourLetterStatus;
  retryInSeconds: number | null;
} {
  if (attempts >= NEIGHBOUR_MAX_ATTEMPTS) {
    return { status: "blocked", retryInSeconds: null };
  }
  return {
    status: "preparing",
    retryInSeconds: neighbourRetrySeconds(attempts),
  };
}

/**
 * A whole-number setting as typed into Tools, or null when there is nothing
 * to save.
 *
 * `Number("")` is 0, and 0 means something in both fields that use this: no
 * lookups at all this month, and no reminders ever. Clearing the box on the
 * way to typing a new number used to save that zero on blur (review item 11
 * on PR #257). Blank, negative, fractional or not a number saves nothing.
 */
export function wholeNumberSetting(
  input: string | null | undefined,
): number | null {
  const value = String(input ?? "").trim();
  if (!/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}
